import type { ImportError } from '../api/generated/ImportError'
import type { ImportReport } from '../api/generated/ImportReport'
import type { ImportRow } from '../api/generated/ImportRow'
import type { Position } from '../api/generated/Position'
import type { Shift } from '../api/generated/Shift'
import { parseCsv } from './csv'

export const MAX_ROWS = 1000

/** A volunteer read from a line of the list, with what prevents importing them. */
export type ParsedRow = { line: number; row: ImportRow; issues: string[] }

export type ParsedList = { rows: ParsedRow[] } | { error: string }

type Column = 'name' | 'firstName' | 'email' | 'phone' | 'shift'

const normalize = (text: string) =>
  text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()

const NAMES: Record<string, Column> = {
  nom: 'name',
  nomcomplet: 'name',
  nomprenom: 'name',
  prenomnom: 'name',
  nomdefamille: 'name',
  name: 'name',
  prenom: 'firstName',
  firstname: 'firstName',
}

function columnOf(header: string): Column | undefined {
  const key = normalize(header).replace(/[^a-z0-9]/g, '')
  if (key.startsWith('creneau')) return 'shift'
  if (key.includes('mail') || key === 'courriel') return 'email'
  if (key.startsWith('tel') || /portable|mobile|phone/.test(key)) return 'phone'
  return NAMES[key]
}

const pad = (n: string) => n.padStart(2, '0')

/** How a list refers to a shift: `Accueil 06/02 08:00`. */
export function shiftReference(shift: Shift, position: Position): string {
  const [date = '', time = ''] = shift.starts_at.split('T')
  const [, month, day] = date.split('-')
  return `${position.name} ${day}/${month} ${time.slice(0, 5)}`
}

// Position name, optional day (`06/02`, `6/2/2027`), start time (`08:00`, `8h`, `8h30`)
const REFERENCE =
  /^(.+?) (?:(\d{1,2})\/(\d{1,2})(?:\/(\d{2}|\d{4}))? )?(\d{1,2})(?::(\d{2})|h(\d{2})?)$/

/** The shifts a reference may designate: position name, optional day, start time. */
export function findShifts(reference: string, positions: Position[], shifts: Shift[]): Shift[] {
  const match = REFERENCE.exec(normalize(reference))
  if (!match) return []
  const [, name, day, month, year, hour = '', minutes, hMinutes] = match
  const positionIds = new Set(positions.filter((p) => normalize(p.name) === name).map((p) => p.id))
  const time = `${pad(hour)}:${minutes ?? hMinutes ?? '00'}`
  return shifts.filter((shift) => {
    const [date = '', clock = ''] = shift.starts_at.split('T')
    const [y = '', m, d] = date.split('-')
    return (
      positionIds.has(shift.position_id) &&
      clock.startsWith(time) &&
      (day === undefined ||
        (Number(d) === Number(day) &&
          Number(m) === Number(month) &&
          (year === undefined || y.endsWith(year))))
    )
  })
}

/**
 * Reads a list pasted from a spreadsheet or exported as CSV: a header line, then one volunteer
 * per line. Headers are recognized in French; every `Créneau…` column holds one shift.
 */
export function readVolunteers(text: string, positions: Position[], shifts: Shift[]): ParsedList {
  const [header, ...records] = parseCsv(text)
  if (!header || records.length === 0)
    return { error: "Collez une ligne d'en-têtes, puis une ligne par bénévole." }
  if (records.length > MAX_ROWS) return { error: `${MAX_ROWS} bénévoles au plus par import.` }
  const columns = header.cells.map(columnOf)
  if (!columns.includes('email'))
    return { error: 'Colonne « Email » introuvable dans la première ligne.' }

  const rows = records.map(({ line, cells }) => {
    const values = (column: Column) =>
      cells.map((cell) => cell.trim()).filter((cell, i) => cell !== '' && columns[i] === column)
    const issues: string[] = []
    const shiftIds = values('shift').flatMap((reference) => {
      const found = findShifts(reference, positions, shifts)
      if (found.length === 1) return found.map((shift) => shift.id)
      issues.push(
        found.length === 0
          ? `Créneau inconnu : « ${reference} »`
          : `Créneau ambigu, précisez le jour : « ${reference} »`,
      )
      return []
    })
    const row: ImportRow = {
      name: [...values('firstName'), ...values('name')].join(' '),
      email: values('email')[0] ?? '',
      phone: values('phone')[0] ?? '',
      shift_ids: shiftIds,
    }
    return { line, row, issues }
  })
  return { rows }
}

export const importErrors: Record<ImportError, string> = {
  missing_email: 'Email manquant',
  invalid_email: 'Email invalide',
  invalid_phone: 'Téléphone invalide',
  invalid_name: 'Nom trop long (200 caractères au plus)',
  duplicate: "Même personne qu'une ligne précédente",
  unknown_shift: 'Créneau supprimé entre-temps',
}

/** Adds the errors of the server's report on `checked`, the rows it was sent, to their issues. */
export function withReport(
  rows: ParsedRow[],
  checked: ParsedRow[],
  report: ImportReport,
): ParsedRow[] {
  const issues = new Map(report.errors.map((e) => [checked[e.row], importErrors[e.error]]))
  return rows.map((row) => {
    const issue = issues.get(row)
    return issue ? { ...row, issues: [issue, ...row.issues] } : row
  })
}

export const isValid = (row: ParsedRow) => row.issues.length === 0

export const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`

/** What importing the checked rows will do, invitations included. */
export function previewSummary(rows: ParsedRow[]): string {
  const valid = rows.filter(isValid)
  const ignored = rows.length - valid.length
  return [
    `${plural(valid.length, 'bénévole', 'bénévoles')} à importer`,
    ignored > 0 && `, ${plural(ignored, 'ligne ignorée', 'lignes ignorées')}`,
    '.',
    valid.length > 0 &&
      ` ${valid.length > 1 ? 'Ils recevront leur invitation à indiquer leurs' : 'Il recevra son invitation à indiquer ses'} disponibilités, sauf si déjà reçue.`,
  ]
    .filter(Boolean)
    .join('')
}
